import { useCallback, useEffect, useState } from 'react'
import { toast } from 'sonner'
import { sitrepService, SITREP_STATUS_LABEL, type Sitrep, type SitrepInput, type SitrepStatus } from '@/services/emergencyService'

const inputCls = 'w-full rounded-xl border border-white/10 bg-white/[0.03] px-3 py-2 text-sm text-white placeholder-gray-600 focus:border-blue-500/50 focus:outline-none'
const btnPrimary = 'rounded-xl bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-500 disabled:opacity-50'
const btnGhost = 'rounded-xl border border-white/10 px-3 py-1.5 text-xs text-gray-300 hover:bg-white/5'

const STATUS_COLOR: Record<SitrepStatus, string> = {
    DRAFT: 'bg-gray-500/15 text-gray-300', SUBMITTED: 'bg-amber-500/15 text-amber-300', APPROVED: 'bg-emerald-500/15 text-emerald-300',
}
const fmt = (iso: string | null) => (iso ? new Date(iso).toLocaleString('es-CL', { dateStyle: 'medium', timeStyle: 'short' }) : '—')
const toLocalInput = (iso: string) => iso.slice(0, 16)
const errorMessage = (e: unknown, fallback: string) =>
    (e as { response?: { data?: { message?: string; error?: string } } })?.response?.data?.message
    ?? (e as { response?: { data?: { error?: string } } })?.response?.data?.error ?? fallback

/**
 * Reportes de situación de una emergencia. Flujo: borrador (mando) → enviado → aprobado
 * (coordinador/administrador), que lo numera y congela la foto de los datos. PDF siempre.
 */
export default function SitrepsTab({ emergencyId, open, canCommand, canApprove }: {
    emergencyId: number; open: boolean; canCommand: boolean; canApprove: boolean
}) {
    const [items, setItems] = useState<Sitrep[]>([])
    const [editing, setEditing] = useState<Sitrep | 'new' | null>(null)
    const [form, setForm] = useState<Required<SitrepInput>>({ periodFrom: '', periodTo: '', situation: '', actions: '', needs: '', outlook: '' })

    const load = useCallback(() => {
        sitrepService.list(emergencyId).then(setItems).catch(() => toast.error('No se pudieron cargar los reportes'))
    }, [emergencyId])
    useEffect(() => { load() }, [load])

    const openNew = () => {
        const now = new Date()
        const from = new Date(now.getTime() - 12 * 3600 * 1000)
        const local = (d: Date) => new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 16)
        setForm({ periodFrom: local(from), periodTo: local(now), situation: '', actions: '', needs: '', outlook: '' })
        setEditing('new')
    }
    const openEdit = (s: Sitrep) => {
        setForm({ periodFrom: toLocalInput(s.periodFrom), periodTo: toLocalInput(s.periodTo), situation: s.situation ?? '', actions: s.actions ?? '', needs: s.needs ?? '', outlook: s.outlook ?? '' })
        setEditing(s)
    }

    const act = async (fn: () => Promise<unknown>, ok: string) => {
        try { await fn(); toast.success(ok); load() } catch (e) { toast.error(errorMessage(e, 'No se pudo aplicar el cambio')) }
    }

    const save = async (ev: React.FormEvent) => {
        ev.preventDefault()
        try {
            if (editing === 'new') await sitrepService.create(emergencyId, form)
            else if (editing) await sitrepService.update(emergencyId, editing.id, form)
            toast.success('Reporte guardado')
            setEditing(null)
            load()
        } catch (e) { toast.error(errorMessage(e, 'No se pudo guardar')) }
    }

    const returnToDraft = (s: Sitrep) => {
        const note = window.prompt('Motivo de la devolución (lo verá quien lo redactó):')
        if (note === null) return
        act(() => sitrepService.returnToDraft(emergencyId, s.id, note), 'Reporte devuelto a borrador')
    }

    return (
        <div className="space-y-4">
            <div className="flex flex-wrap items-center justify-between gap-3">
                <p className="text-xs text-gray-500">
                    Cada período operativo se redacta un borrador, se envía a aprobación y, al aprobarse, queda numerado con una
                    foto fija de los datos del evento. El PDF de un reporte aprobado no cambia aunque la bitácora siga creciendo.
                </p>
                {canCommand && open && <button onClick={openNew} className={btnPrimary}>Nuevo reporte</button>}
            </div>

            {editing && (
                <form onSubmit={save} role="dialog" aria-label={editing === 'new' ? 'Nuevo reporte de situación' : 'Editar reporte de situación'}
                    className="space-y-3 rounded-xl border border-white/10 p-4">
                    <div className="grid grid-cols-2 gap-3">
                        <label className="text-xs text-gray-400">Desde
                            <input type="datetime-local" required value={form.periodFrom} onChange={(e) => setForm({ ...form, periodFrom: e.target.value })} className={`${inputCls} mt-1`} />
                        </label>
                        <label className="text-xs text-gray-400">Hasta
                            <input type="datetime-local" required value={form.periodTo} onChange={(e) => setForm({ ...form, periodTo: e.target.value })} className={`${inputCls} mt-1`} />
                        </label>
                    </div>
                    {([['situation', 'Situación actual'], ['actions', 'Acciones realizadas'], ['needs', 'Necesidades y solicitudes'], ['outlook', 'Proyección']] as [keyof SitrepInput, string][]).map(([k, label]) => (
                        <label key={k} className="block text-xs text-gray-400">{label}
                            <textarea aria-label={label} rows={3} value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })} className={`${inputCls} mt-1`} />
                        </label>
                    ))}
                    <div className="flex justify-end gap-3">
                        <button type="button" onClick={() => setEditing(null)} className={btnGhost}>Cancelar</button>
                        <button type="submit" className={btnPrimary}>Guardar borrador</button>
                    </div>
                </form>
            )}

            <ul className="space-y-2">
                {items.length === 0 && <li className="text-xs text-gray-500">Sin reportes de situación.</li>}
                {items.map((s) => (
                    <li key={s.id} className="rounded-xl border border-white/10 p-3">
                        <div className="flex flex-wrap items-center gap-2 text-sm">
                            <span className="font-semibold text-white">{s.number != null ? `Reporte N° ${s.number}` : 'Borrador'}</span>
                            <span className={`rounded-full px-2 py-0.5 text-[10px] uppercase ${STATUS_COLOR[s.status]}`}>{SITREP_STATUS_LABEL[s.status]}</span>
                            <span className="text-xs text-gray-500">{fmt(s.periodFrom)} → {fmt(s.periodTo)}</span>
                            <span className="ml-auto flex flex-wrap gap-2">
                                <a href={sitrepService.pdfUrl(emergencyId, s.id)} target="_blank" rel="noopener noreferrer" className={btnGhost}>PDF</a>
                                {canCommand && open && s.status === 'DRAFT' && <>
                                    <button onClick={() => openEdit(s)} className={btnGhost}>Editar</button>
                                    <button onClick={() => act(() => sitrepService.submit(emergencyId, s.id), 'Enviado a aprobación')} className={btnGhost}>Enviar</button>
                                    <button onClick={() => window.confirm('¿Borrar este borrador?') && act(() => sitrepService.remove(emergencyId, s.id), 'Borrador eliminado')} className="text-xs text-rose-300 hover:underline">Borrar</button>
                                </>}
                                {canApprove && open && s.status === 'SUBMITTED' && <>
                                    <button onClick={() => act(() => sitrepService.approve(emergencyId, s.id), 'Reporte aprobado')} className="rounded-xl bg-emerald-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-emerald-500">Aprobar</button>
                                    <button onClick={() => returnToDraft(s)} className={btnGhost}>Devolver</button>
                                </>}
                            </span>
                        </div>
                        {s.returnNote && s.status === 'DRAFT' && <p className="mt-1 text-xs text-amber-300">Devuelto: {s.returnNote}</p>}
                        {s.situation && <p className="mt-2 line-clamp-2 text-xs text-gray-400">{s.situation}</p>}
                        <p className="mt-1 text-[11px] text-gray-600">
                            Redactado por {s.createdName ?? '—'} · {fmt(s.createdAt)}{s.approvedAt ? ` · aprobado por ${s.approvedName ?? '—'} el ${fmt(s.approvedAt)}` : ''}
                        </p>
                    </li>
                ))}
            </ul>
        </div>
    )
}
